<template>
  <div>
    <data-table
      :key="tableKey"
      :tableOpts="tableOpts"
      :loadData="loadData"
      :apiMode="true"
      @cell-clicked="onCellClicked"
    />

    <list-item-form
      v-if="showModal"
      :item="itemToEdit"
      :config="modalConfig"
      @close="closeModal"
      @refresh-table="refreshTable"
    />
    <ReplaceCategory
      v-if="showReplaceModal"
      :isActive="showReplaceModal"
      :categoryToDelete="categoryToDelete"
      :ptCount="ptCount"
      :asmbCount="asmbCount"
      :vendorCount="vendorCount"
      @close-modal="onReplaceModalClose"
    />
  </div>
</template>

<script>
import { reactive, toRefs } from 'vue';
import { cloneDeep, isEmpty, uniq, map, forEach, some, trim } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import { ModalProgrammatic } from '@oruga-ui/oruga-next';
import DataTable from '@/components/table-fields/DataTable.vue';
import ExcelImport from '@/components/ExcelImport.vue';
import Category from '@/models/Vendors';
import Catalogs from '@/models/Catalogs';
import ListItemForm from '@/components/modals/ListItemForm.vue';
import ReplaceCategory from '@/components/modals/ReplaceCategory.vue';

export default {
  name: 'MaterialCategories',
  props: {
    searchText: {
      type: String,
      default: '',
    },
  },
  components: {
    'data-table': DataTable,
    'list-item-form': ListItemForm,
    ReplaceCategory,
  },
  setup(props) {
    const toast = useToast();

    const data = reactive({
      tableKey: 0,
      isLoading: false,
      showModal: false,
      itemToEdit: {},
      showReplaceModal: false,
      categoryToDelete: null,
      ptCount: 0,
      asmbCount: 0,
      vendorCount: 0,
      categories: [],
    });

    const refreshTable = () => { data.tableKey += 1; };

    const openModal = (item = {}) => {
      data.itemToEdit = cloneDeep(item);
      data.showModal = true;
    };

    const closeModal = () => {
      data.showModal = false;
      data.itemToEdit = {};
    };

    const onReplaceModalClose = () => {
      data.showReplaceModal = false;
      data.categoryToDelete = null;
      data.ptCount = 0;
      data.asmbCount = 0;
      data.vendorCount = 0;
      refreshTable();
    };

    const modalConfig = {
      label: 'Material Category',
      showSubCategories: true,
      showColor: false,
      saveFn: async (payload) => Category.addCategories([payload]),
      updateFn: async (payload) => Category.updateCategories(payload),
    };

    const tableOpts = {
      defaultSortField: 'name',
      defaultSortOrder: 'asc',
      stickeyHeaders: false,
      fields: [
        {
          title: 'Name',
          id: 'name',
          prop: 'name',
          name: 'field-generic-input',
          width: '400px',
          visible: true,
          alwaysVisible: true,
          inputProps: { type: 'text', openCard: false },
        },
        {
          title: 'Sub Categories',
          name: 'category-count-icon',
          width: '112px',
          id: 'subCategory',
          prop: 'subCategories',
          defaultVisible: true,
        },
        {
          title: 'ACTIONS',
          name: 'table-field-actions',
          id: 'action',
          hideFromChooser: true,
          alwaysVisible: true,
          defaultVisible: true,
          width: '112px',
          permissionModule: 'app&projects',
          permissionKey: 'company_lists',
          inputProps: {
            showEdit: false,
            groupButtons() {
              return [
                {
                  icon: 'icon-edit',
                  event: 'edit',
                  tooltip: 'Edit Category',
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
                {
                  icon: 'icon-bin',
                  event: 'delete',
                  tooltip: 'Delete Category',
                  disabled: (data) => data.isDefault,
                  permissionModule: 'app&projects',
                  alterDataKey: 'lists',
                },
              ];
            },
          },
        },
      ],
    };

    const loadData = async (params) => {
      const queryParams = {
        limit: params.limit || 25,
        page: params.page || 1,
        search: props.searchText || '',
        sort: params.sort,
      };
      const [catResult, subCatResult] = await Promise.all([
        Category.getCategories(queryParams),
        Category.getCategories({ isParent: false, limit: 500 }),
      ]);
      const subCatsByParent = {};
      (subCatResult.data ?? []).forEach((sc) => {
        const parentId = sc.parent?._id || sc.parent;
        if (parentId) {
          if (!subCatsByParent[parentId]) subCatsByParent[parentId] = [];
          subCatsByParent[parentId].push(sc);
        }
      });
      const result = (catResult.data ?? []).map((cat) => ({
        ...cat,
        subCategories: subCatsByParent[cat._id] || [],
      }));
      data.categories = result;
      return { data: result, total: catResult.total ?? 0 };
    };

    const onDeleteCategory = (rowData) => {
      DialogProgrammatic.confirm({
        title: 'Delete Category',
        message: `Are you sure you want to delete "${rowData.name}"?`,
        cancelButton: 'Cancel',
        type: 'danger',
        onClose: () => {
          data.isCheckable = false;
          data.selectedRows = [];
        },onConfirm: async () => {
          try {
            await Category.archiveCategory({
              categoryId: rowData._id,
              newCategoryId: null,
              newSubCategoryId: null,
              replace: false,
            });
            toast.success('Category deleted successfully');
            refreshTable();
          } catch (e) {
            toast.error('Error deleting category');
          }
        },
      });
    };

    const onCellClicked = async ({ type, data: rowData }) => {
      if (type === 'edit') {
        openModal(rowData);
      } else if (type === 'delete') {
        data.isLoading = true;
        try {
          const [catalogResult, vendorResult] = await Promise.all([
            Catalogs.getAssemblyParts({
              categoryId: rowData._id,
              showItems: 'show-archived-catalogs',
              showAssmbsParts: true,
              limit: 9999,
            }),
            Category.getVendors({ limit: 9999, categoryId: rowData._id }),
          ]);

          let ptCount = 0;
          let asmbCount = 0;
          (catalogResult.data ?? []).forEach((item) => {
            if (item.__t === 'UpcItems') ptCount++;
            else asmbCount++;
          });
          const vendorCount = vendorResult?.data?.length ?? 0;

          if (ptCount + asmbCount + vendorCount === 0) {
            onDeleteCategory(rowData);
          } else {
            data.categoryToDelete = rowData;
            data.ptCount = ptCount;
            data.asmbCount = asmbCount;
            data.vendorCount = vendorCount;
            data.showReplaceModal = true;
          }
        } catch (e) {
          toast.error('Error loading category data');
        } finally {
          data.isLoading = false;
        }
      }
    };

    const mapSubCategories = (items) => {
      const newItems = [];
      let newItem = {};
      items.forEach((item, idx) => {
        if (!isEmpty(item.name)) {
          if (idx !== 0) { newItems.push(newItem); newItem = {}; }
          newItem.name = item.name;
        }
        if (!isEmpty(item.subCategories)) {
          const subcategory = item.subCategories.split(',');
          forEach(subcategory, (subCat) => {
            if (isEmpty(newItem.subCategories)) newItem.subCategories = [];
            newItem.subCategories.push({ name: subCat });
          });
        } else {
          newItem.subCategories = [];
        }
        newItem.fromExcel = true;
        if (items.length === idx + 1 && !isEmpty(newItem)) {
          newItems.push(newItem);
          newItem = {};
        }
      });
      return newItems;
    };

    const importExcel = () => {
      ModalProgrammatic.open({
        component: ExcelImport,
        props: {
          batchSize: 200,
          colsToUse: 'categoryCols',
          importModule: 'category-import',
          isExcelModalActive: true,
          uniqueFields: ['name'],
          saveExcelItem: async (obj, fileName, isFinalBatch) => {
            obj = mapSubCategories(obj);
            const catName = uniq(map(obj, (category) => category.name.toUpperCase()));
            if (catName.length !== obj.length) {
              DialogProgrammatic.confirm({
                message: 'Category Names cannot be the same',
                okButton: 'OK',
              });
              return;
            }
            let result;
            if (!isEmpty(obj)) result = await Category.addCategories(obj);
            if (isFinalBatch) {
              toast.success('Successfully imported Categories');
              refreshTable();
            }
            return result;
          },
          validateExcelItem: (obj) => validationChecks(obj),
        },
        events: { 'close-modal': () => refreshTable() },
        canCancel: true,
      });
    };

    const validationChecks = (category) => {
      category = mapSubCategories([category])[0];
      const uniqCategory = some(data.categories, (cat) => {
        if (!isEmpty(category.name)) {
          return (
            category._id !== cat._id
            && cat.name.toUpperCase() === category.name.toUpperCase()
          );
        }
        return false;
      });
      let errMsg = null;
      if (isEmpty(category.name)) {
        errMsg = 'Category Name cannot be empty';
      } else if (trim(category.name).length < 3) {
        errMsg = 'Category name should be atleast 3 characters long';
      } else if (
        some(category.subCategories, (subCat) => isEmpty(subCat.name))
      ) {
        errMsg = 'Subcategory Name cannot be empty';
      } else if (
        isEmpty(category.subCategories)
        && some(
          category.subCategories,
          (subCat) => trim(subCat.name).length < 3,
        )
      ) {
        errMsg = 'Subcategory name should be atleast 3 characters long';
      } else if (!isEmpty(category.name.trim()) && uniqCategory) {
        errMsg = `The Category ${category.name} is Already Present`;
      } else {
        const subcat = uniq(map(category.subCategories, (subCat) => subCat.name.toUpperCase()));
        if (
          !isEmpty(category.subCategories)
          && category.subCategories.length !== subcat.length
        ) {
          errMsg = 'Subcategory Name cannot be the same';
        }
      }
      return errMsg;
    }

    return {
      ...toRefs(data),
      modalConfig,
      tableOpts,
      loadData,
      onCellClicked,
      refreshTable,
      openModal,
      closeModal,
      importExcel,
      onReplaceModalClose,
      validationChecks,
    };
  },
};
</script>

<style scoped>
</style>
